import { useState } from 'react';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES VIDÉOS ET LES PHOTOS DU SITE — SERVIES DEPUIS LE DOMAINE DU PRODUIT, ET
 * JAMAIS EMBARQUÉES D'UN TIERS.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-73, et les deux motifs sont écrits au cahier des charges « parce qu'ils ne se
 * déduisent pas » :
 *
 * > « Les deux vidéos publicitaires et les photos sont servies **depuis le domaine du
 * > produit**, jamais embarquées depuis un hébergeur de vidéos. Deux motifs : le principe
 * > du parc met hébergement et outillage en UE avec **une seule entorse, datée et bornée à
 * > la visio** ; et **un lecteur tiers enverrait l'adresse réseau de chaque visiteur à ce
 * > tiers**, sur une surface qui vient de refuser toute mesure d'audience. »
 *
 * Et F-73 encore : « **une seule ressource tierce sur toute la surface**, le bloc du
 * formulaire de contact, sur sa page. **Aucune police, aucune image, aucun script, aucun
 * lecteur chargé depuis un tiers ailleurs.** »
 *
 * **CE COMPOSANT NE PREND DONC AUCUNE ADRESSE ABSOLUE, ET C'EST LA FORME DE LA RÈGLE.**
 * Il prend un **chemin** sous `/media/`, servi par la même image que la page. Une propriété
 * `src` libre aurait laissé n'importe quelle page poser l'adresse d'un hébergeur, et la
 * règle serait redevenue une consigne de relecture. La politique de sécurité de contenu la
 * tient une seconde fois — `img-src` et `media-src` ne portent aucun domaine de tiers.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **AUCUNE CAPTURE D'ÉCRAN RÉELLE DU PRODUIT NE PASSE PAR ICI, ET CE N'EST PAS À CE
 * COMPOSANT DE LE VÉRIFIER.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `CDC-6` en fait « le fait le plus lourd de ce document, et il ferme un risque **par
 * construction plutôt que par une règle** » : « aucune capture d'écran réelle du produit ne
 * paraît sur cette surface, donc **aucune écriture manuscrite, aucun prénom, aucun titre de
 * cours, aucun contenu de tableau, aucune donnée de mineur**. Et ce n'est pas parce qu'on
 * les filtre, **c'est parce qu'on n'en met aucune.** »
 *
 * **Aucun code ne peut tenir cette règle**, et prétendre le contraire serait le vrai
 * danger : elle porte sur **ce que contient un fichier livré**, ce qu'aucun contrôle
 * n'inspecte. Ce qui la tient est que les fichiers sont produits « hors du produit, une
 * fois, à la fabrication du contenu » (F-74), donc **par une personne qui décide**, et que
 * le dépôt n'a aucun chemin par lequel une capture d'écran du produit arriverait toute
 * seule dans `site/public/media/`.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LES FICHIERS NE SONT PAS ENCORE LIVRÉS, ET LE COMPOSANT LE DIT AU LIEU DE
 * L'INVENTER.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Les deux vidéos et les photos générées sont **du contenu à produire**, prérequis 30 de
 * `docs/PREREQUIS-EXTERNES.md`, ouvert. Tant qu'un fichier manque, ce composant affiche un
 * bloc qui **nomme ce qui manque**, et c'est la conduite que D-075 a fixée pour tout ce que
 * le produit n'a pas : « affiche l'indisponibilité, **ne l'invente pas** », et « un
 * indicateur vide se lit comme une panne » si personne ne dit pourquoi.
 *
 * **Le repli se déclenche au chargement et non à la construction**, parce que l'image de
 * `site/` sert une application à page unique : nginx renvoie `index.html` sur une adresse
 * inconnue, donc un média absent arrive comme **du HTML que le lecteur ne sait pas
 * décoder**, jamais comme un 404. C'est `onError` qui le voit, et lui seul.
 */

/** Le seul dossier d'où ce site sert un média. Aucune adresse absolue n'entre ici. */
const DOSSIER = '/media';

type ProprietesCommunes = {
  /** Le nom du fichier, sous `site/public/media/`. Jamais une adresse. */
  readonly fichier: string;
  /** Ce que le média montre, pour qui ne le voit pas ou ne peut pas le charger. */
  readonly description: string;
};

function BlocIndisponible({ description }: { readonly description: string }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-4 border border-bord bg-fond-bloc px-6 py-10 text-center">
      <p className="text-14 font-weight-700 text-texte">{description}</p>
      <p className="text-13 text-texte-faible">
        Ce média n’est pas encore livré.
      </p>
    </div>
  );
}

/**
 * **Une vidéo publicitaire — il y en a DEUX sur toute la surface**, F-74, et le cahier des
 * charges ne dit pas laquelle va où : « la répartition des deux vidéos entre l'accueil et la
 * page du détail » est une décision de composition, et le brief de maquette la range
 * explicitement parmi ce qui **n'est pas une décision de spécification**.
 *
 * **Aucune lecture automatique, et c'est un choix qui se justifie** : une vidéo qui se lance
 * seule télécharge son fichier chez tout le monde, y compris chez qui ne la regardera pas,
 * et une surface qui refuse de compter ses visiteurs n'a pas à leur imposer un
 * téléchargement. `preload="metadata"` ne prend que de quoi afficher la durée.
 */
export function Video({ fichier, description }: ProprietesCommunes) {
  const [perdue, poserPerdue] = useState(false);

  if (perdue) return <BlocIndisponible description={description} />;

  return (
    <video
      controls
      preload="metadata"
      onError={() => poserPerdue(true)}
      className="w-full rounded-4 bg-fond-bloc"
    >
      <source src={`${DOSSIER}/${fichier}`} />
      {description}
    </video>
  );
}

/**
 * **Une photo générée par un modèle d'image, produite HORS du produit.**
 *
 * F-74 : « les photos sont générées **hors du produit, une fois, à la fabrication du
 * contenu**, et livrées comme des fichiers avec le code. **Rien du produit n'appelle un
 * modèle de langage ou d'image, à l'exécution ni ailleurs.** » Et la conséquence « va dans
 * le bon sens : ces images **ne représentent aucune personne réelle**, ce qui renforce le
 * refus de toute capture d'écran réelle. »
 *
 * **`loading="lazy"` n'est pas une mesure d'audience**, et la distinction vaut d'être
 * écrite sur cette surface : elle ne dépose rien, ne compte rien et n'informe personne — le
 * navigateur diffère une requête vers **le domaine du produit**, qui est déjà celui de la
 * page.
 */
export function Photo({ fichier, description }: ProprietesCommunes) {
  const [perdue, poserPerdue] = useState(false);

  if (perdue) return <BlocIndisponible description={description} />;

  return (
    <img
      src={`${DOSSIER}/${fichier}`}
      alt={description}
      loading="lazy"
      onError={() => poserPerdue(true)}
      className="w-full rounded-4 bg-fond-bloc object-cover"
    />
  );
}
